/**
 * Helpers for keeping the user's place while a table lazy-loads more rows.
 *
 * Kept free of React and the DOM so the decisions can be tested directly —
 * the tricky part isn't the wiring, it's deciding *when* to intervene.
 */

/** Pixels of upward movement below which we assume the user did it, not us. */
export const SCROLL_JUMP_THRESHOLD = 40;

/**
 * True when `nextIds` is `prevIds` plus rows at the end — i.e. a lazy-loaded
 * batch rather than a new result set (filter, tab switch, refetch, sort).
 *
 * Rows without ids can't be compared meaningfully, so they're never treated
 * as an append; otherwise any growing list would qualify.
 */
export function isAppendOnly(prevIds: any[], nextIds: any[]): boolean {
  if (prevIds.length === 0) return false;
  if (nextIds.length <= prevIds.length) return false;
  return prevIds.every(
    (id, i) => id !== undefined && id !== null && nextIds[i] === id,
  );
}

/**
 * True when the scroller was thrown BACKWARDS far enough that it must have
 * been the render, not the user.
 *
 * Only upward jumps count: scrolling further down between the last scroll
 * event and the commit is the user moving, and undoing that would fight them.
 */
export function shouldRestoreScroll(
  rememberedTop: number,
  currentTop: number,
  threshold: number = SCROLL_JUMP_THRESHOLD,
): boolean {
  return rememberedTop - currentTop > threshold;
}
